import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Inventory as InventoryIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  FilterList as FilterListIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ownAuthSession from '../../services/auth/ownAuthSession';
import firebaseDataService from '../../services/FirebaseDataService';
import { accountingApi } from '../../config/apiClient';

// Kiosk ürün arayüzü (YENİ: InventoryItems tablosundan)
interface KioskProduct {
  id: string; // ✅ YENİ: Guid (string)
  companyId: string;
  name: string;
  stockCode?: string;
  barcode?: string;
  categoryId?: string;
  categoryName?: string;
  brandId?: string;
  brandName?: string;
  unit: string;
  salePrice: number;
  saleCurrency?: string;
  description?: string;
  imageUrl?: string;
  isActive: boolean;
  isKiosk: boolean;
  kioskSentAt?: string;
  kioskLastUpdate?: string;
}

const KioskProducts: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('salesSystem');

  // State tanımlamaları
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<KioskProduct[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<KioskProduct[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sayfalama için state'ler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Arama ve filtreleme
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Menü için state'ler
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<KioskProduct | null>(null);

  // Dialog state'leri
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false); // ✅ YENİ: Silme işlemi için ayrı loading state

  // Snackbar state'leri
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('kioskProductsPage.errors.userNotLoggedIn'));
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 [KIOSK PRODUCTS] Şirket ID alınıyor...', { userId: uid });
        const userData = await firebaseDataService.getUserData(uid);

        if (userData) {
          console.log('✅ [KIOSK PRODUCTS] Kullanıcı bilgileri alındı', {
            companyId: userData.companyId,
            role: userData.role,
          });
          setCompanyId(userData.companyId || null);
        } else {
          setError(t('kioskProductsPage.errors.userInfoNotFound'));
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ [KIOSK PRODUCTS] Şirket ID alınırken hata:', error);
        setError(t('kioskProductsPage.errors.companyInfoLoadFailed'));
        setLoading(false);
      }
    };

    fetchCompanyId();
  }, []);

  // Ürünleri yükle (YENİ: InventoryItems tablosundan IsKiosk=true olanlar)
  const loadProducts = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      console.log('🔍 [KIOSK PRODUCTS V2] Ürünler yükleniyor (InventoryItems)...', { companyId });

      const response = await accountingApi.get(`/api/Inventory/kiosk-items`);
      const result = response.data;
      if (result.success && result.data) {
        setProducts(result.data);
        console.log('✅ [KIOSK PRODUCTS V2] Ürünler yüklendi (InventoryItems)', { count: result.data.length });
      } else {
        console.log('ℹ️ [KIOSK PRODUCTS V2] Henüz ürün bulunamadı');
        setProducts([]);
      }
    } catch (error) {
      console.error('❌ [KIOSK PRODUCTS V2] Ürünler yüklenirken hata:', error);
      setError(t('kioskProductsPage.errors.productsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [companyId]);

  // Arama ve filtreleme
  useEffect(() => {
    let filtered = [...products];

    // Arama filtresi
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(lowerSearch) ||
          product.stockCode?.toLowerCase().includes(lowerSearch) ||
          product.barcode?.toLowerCase().includes(lowerSearch) ||
          product.categoryName?.toLowerCase().includes(lowerSearch) ||
          product.brandName?.toLowerCase().includes(lowerSearch)
      );
    }

    // Aktiflik filtresi
    if (!showInactive) {
      filtered = filtered.filter((product) => product.isActive);
    }

    setFilteredProducts(filtered);
    setPage(0);
  }, [products, searchTerm, showInactive]);

  // Yardımcı fonksiyonlar
  const getCurrencySymbol = (currency?: string): string => {
    switch (currency?.toUpperCase()) {
      case 'TRY':
      case 'TL':
        return '₺';
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      default:
        return '₺';
    }
  };

  // Event handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: KioskProduct) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleDeleteClick = () => {
    // ✅ selectedProduct zaten setMenuOpen'da set edilmiş, sakla!
    console.log('🗑️ [KIOSK PRODUCTS] handleDeleteClick - selectedProduct:', selectedProduct);
    setDeleteDialogOpen(true);
    // ❌ handleMenuClose() YAPMA! Çünkü bu selectedProduct'ı null yapıyor
    setMenuAnchorEl(null); // Sadece menüyü kapat, selectedProduct'ı koru
  };

  const handleDeleteConfirm = async () => {
    console.log('🔘 [KIOSK PRODUCTS] Kapat butonuna basildi!');
    console.log('🔍 [KIOSK PRODUCTS] selectedProduct:', selectedProduct);
    console.log('🔍 [KIOSK PRODUCTS] companyId:', companyId);
    
    if (!selectedProduct) {
      console.error('❌ [KIOSK PRODUCTS] selectedProduct bulunamadi!');
      return;
    }
    
    if (!companyId) {
      console.error('❌ [KIOSK PRODUCTS] companyId bulunamadi!');
      return;
    }

    try {
      setIsDeleting(true); // ✅ Ayrı loading state kullan
      console.log('✅ [KIOSK PRODUCTS] setIsDeleting(true) yapildi');
      
      console.log('🔄 [KIOSK PRODUCTS V2] Kiosk\'tan kapatılıyor:', {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        companyId
      });

      const response = await accountingApi.post(`/api/Inventory/${selectedProduct.id}/toggle-kiosk`, { enable: false });
      const result = response.data;
      console.log('✅ [KIOSK PRODUCTS V2] API success:', result);

      setSnackbar({
        open: true,
        message: t('kioskProductsPage.messages.closedFromKiosk'),
        severity: 'success',
      });

      // Modalı hemen kapat
      setDeleteDialogOpen(false);
      setSelectedProduct(null);

      // Listeyi yenile
      await loadProducts();
    } catch (err) {
      console.error('❌ [KIOSK PRODUCTS V2] Ürün Kiosk\'tan kapatma hatası:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : t('kioskProductsPage.errors.closeFromKioskFailed'),
        severity: 'error',
      });
    } finally {
      console.log('🏁 [KIOSK PRODUCTS] finally blogu - setIsDeleting(false)');
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedProduct(null);
  };

  // Sayfa değişimi
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalandırılmış ürünler
  const paginatedProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && !products.length) {
    return (
      <Container maxWidth={false} sx={{ height: '100vh', overflow: 'hidden', px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('kioskProductsPage.loading')}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ height: '100vh', overflow: 'hidden', px: { xs: 1, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
          {t('kioskProductsPage.actions.refreshPage')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: '100vh', overflow: 'auto', px: { xs: 1, sm: 2, md: 3 }, pb: 16 }}>
      <Helmet>
        <title>{t('kioskProductsPage.documentTitle')}</title>
      </Helmet>

      <Box sx={{ py: 2 }}>
        {/* Başlık */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => navigate('/kiosk-menu')}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <InventoryIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {t('kioskProductsPage.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('kioskProductsPage.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Arama ve Filtreler */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder={t('kioskProductsPage.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant={showInactive ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setShowInactive(!showInactive)}
              size="small"
            >
              {showInactive ? t('kioskProductsPage.actions.showAll') : t('kioskProductsPage.actions.showActiveOnly')}
            </Button>
          </Box>
        </Paper>

        {/* İstatistikler */}
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <Paper sx={{ p: 2, flexGrow: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">
              {t('kioskProductsPage.stats.totalProducts')}
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              {products.length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flexGrow: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">
              {t('kioskProductsPage.stats.activeProducts')}
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main">
              {products.filter((p) => p.isActive).length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flexGrow: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">
              {t('kioskProductsPage.stats.filtered')}
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="info.main">
              {filteredProducts.length}
            </Typography>
          </Paper>
        </Box>

        {/* Ürün Listesi */}
        <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
        {filteredProducts.length === 0 ? (
          <Box p={4} textAlign="center">
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {searchTerm
                ? t('kioskProductsPage.empty.searchNoResult')
                : t('kioskProductsPage.empty.noOpenProducts')}
            </Alert>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 200,
                      }}
                    >
                      {t('kioskProductsPage.table.product')}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 120,
                      }}
                    >
                      {t('kioskProductsPage.table.stockCode')}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 120,
                      }}
                    >
                      {t('kioskProductsPage.table.barcode')}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 100,
                      }}
                    >
                      {t('kioskProductsPage.table.category')}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 100,
                      }}
                    >
                      {t('kioskProductsPage.table.brand')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 120,
                      }}
                    >
                      {t('kioskProductsPage.table.salePrice')}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 80,
                      }}
                    >
                      {t('kioskProductsPage.table.status')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: 80,
                      }}
                    >
                      {t('kioskProductsPage.table.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((product) => (
                    <TableRow
                      key={product.id}
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        opacity: product.isActive ? 1 : 0.6,
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {product.imageUrl ? (
                            <Avatar
                              src={product.imageUrl}
                              alt={product.name}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                              <ImageIcon />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {product.name}
                            </Typography>
                            {product.description && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                {product.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.stockCode || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.barcode || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.categoryName || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{product.brandName || '-'}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {product.salePrice.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          {getCurrencySymbol(product.saleCurrency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.isActive ? t('kioskProductsPage.status.active') : t('kioskProductsPage.status.inactive')}
                          color={product.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '11px', height: 24 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, product)} title={t('kioskProductsPage.table.actions')}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Sayfalama */}
            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={t('kioskProductsPage.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => t('kioskProductsPage.pagination.displayedRows', { count, from, to })}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          </>
        )}
        </Paper>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'warning.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary={t('kioskProductsPage.rowActions.closeFromKiosk')} />
        </MenuItem>
      </Menu>

      {/* Kiosk'tan Kapatma Onay Modalı */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { 
            borderRadius: 2,
            minWidth: 400
          },
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'warning.main', 
          color: 'white',
          fontWeight: 'bold'
        }}>
          {t('kioskProductsPage.closeDialog.title')}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ fontSize: '1rem' }}>
            {t('kioskProductsPage.closeDialog.description', { name: selectedProduct?.name || '' })}
            <br />
            <br />
            {t('kioskProductsPage.closeDialog.info')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="inherit"
            variant="outlined"
          >
            {t('kioskProductsPage.actions.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="warning" 
            variant="contained" 
            autoFocus
            disabled={isDeleting}
          >
            {isDeleting ? t('kioskProductsPage.actions.closing') : t('kioskProductsPage.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </Container>
  );
};

export default KioskProducts;

